import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { keys } from '@/lib/queryKeys';
import { fetchAppointmentSlots } from './api';
import type { AppointmentSlotQuery } from './types';

/**
 * Branch-scoped — see HMS_V4_API_CONTRACT.md §5. `branchVersion` is appended
 * for the same reason useTodayQueue.ts appends it: it forces a re-render at
 * the exact moment getActiveBranchId() already reflects a branch switch,
 * closing the race documented in queryKeys.ts's invalidateBranchScoped.
 *
 * Disabled until both doctorId and date are chosen — mirrors the legacy
 * `if (!doctorId || !date) return;` guard in AppointmentsModule.jsx exactly:
 * no request fires for an incomplete selection.
 */
export function useAppointmentSlots(params: AppointmentSlotQuery) {
  const { branchVersion } = useAuth();
  return useQuery({
    // Cast, not a widened queryKeys.ts signature: AppointmentSlotQuery has
    // named optional fields (no index signature), which is a stricter shape
    // than Record<string, unknown>, not an incompatible one — every value in
    // it is still plain, structurally-comparable data for cache identity.
    queryKey: [...keys.appointments.slots(params as Record<string, unknown>), branchVersion],
    queryFn: () => fetchAppointmentSlots(params),
    enabled: !!params.doctor_id && !!params.date,
  });
}
